import { Server, LifeBuoy, Phone, Globe, Workflow, UserRound, Accessibility, ShieldCheck, Home, FileCheck, Scale, LineChart, type LucideIcon } from "lucide-react";

export type Service = {
  slug: string;
  title: string;
  shortTitle: string;
  excerpt: string;
  description: string;
  icon: LucideIcon;
  highlights: string[];
  seoDescription: string;
  heroSubtitle?: string;
  heroCtaLabel?: string;
  heroBestFor?: string;
  heroImage?: string;
  heroImageClass?: string;
  heroImagePosition?: string;
};

export const services: Service[] = [
  {
    slug: "managed-it",
    title: "Managed IT Services",
    shortTitle: "Managed IT",
    excerpt: "Proactive IT management that keeps your business running smoothly, securely, and with minimal downtime.",
    description:
      "Managed IT services that keep your business running smoothly, securely, and with minimal downtime. Get proactive local support, data protection, cloud solutions, and expert IT management tailored to your operations. Reduce risks, improve efficiency, and stay ahead with dependable technology solutions.",
    icon: Server,
    highlights: [
      "24/7 proactive monitoring and patching",
      "Data backup and disaster recovery",
      "Cloud strategy, migration and optimization",
      "Vendor management and IT roadmap planning",
    ],
    seoDescription: "Reliable managed IT services for Montclair businesses — proactive monitoring, cloud, backup, and local expert support.",
  },
  {
    slug: "tech-support",
    title: "Tech Support Services",
    shortTitle: "Tech Support",
    excerpt: "Responsive tech support for small businesses, nonprofits, and offices—keeping your team productive.",
    description:
      "When technology breaks, productivity stops. We provide responsive tech support for small businesses, nonprofits, and offices—helping you resolve issues fast and keep work moving.",
    icon: LifeBuoy,
    highlights: [
      "Smartphone, laptop and desktop troubleshooting",
      "Software and hardware setup",
      "Email, calendar and account recovery",
      "Same-day local response when possible",
    ],
    seoDescription: "Responsive tech support for small businesses, nonprofits, and offices—resolve stalled computers, email problems, and malware fast.",
    heroSubtitle: "So your busy team can keep working",
    heroCtaLabel: "GET HELP NOW",
    heroBestFor: "Best for your small office that have a stalled computer, email problems, or malware issues that need fixed right now.",
  },
  {
    slug: "voip",
    title: "Business VoIP Phone Systems Built for Southern California Businesses",
    shortTitle: "VoIP",
    excerpt: "Cut your phone bill, ditch the dropped calls, and finally have a system your team can actually use — anywhere they work.",
    description:
      "When your phone system fails, your business stops. SimpleTech designs, installs, and supports custom Voice over IP (VoIP) phone systems for small and mid-sized businesses across Montclair, the Inland Empire, and Orange County. As a vendor-neutral partner to 50+ VoIP providers — including RingCentral, Zoom Phone, Spectrum Business, Nextiva, 8x8, Vonage, Ooma, and more — we match you to the right platform instead of selling you whichever one pays us the most.",
    icon: Phone,
    highlights: [
      "HD voice with secure, encrypted calling",
      "Mobile and desktop softphone apps",
      "Auto-attendant, voicemail-to-email, call routing",
      "Scales as your team grows — no rip and replace",
    ],
    seoDescription: "Business VoIP solutions in Southern California. Partnered with 50+ providers — RingCentral, Zoom, Spectrum & more. Save up to 60% on phone bills. Free quote.",
    heroSubtitle: "Cut your phone bill, ditch the dropped calls, and finally have a system your team can actually use — anywhere they work.",
    heroCtaLabel: "GET MY FREE VoIP QUOTE",
    heroBestFor: "30-minute consultation, written quote in 2 business days, no obligation.",
  },
  {
    slug: "website-design",
    title: "Website Design That Actually Brings You Business",
    shortTitle: "Small Business Website Design",
    excerpt: "Fast, mobile-friendly, search-optimized websites built by a team that answers the phone.",
    description:
      "Your website is the first impression most customers ever have of your business. SimpleTech designs and builds professional websites for small businesses — sites that load fast, look sharp on every device, rank on Google, and convert visitors into paying customers. We handle everything from copywriting and design to hosting, SEO, and ongoing maintenance, so you can focus on running your business instead of fighting with WordPress.",
    icon: Globe,
    highlights: [
      "Custom mobile-first design tailored to your brand",
      "On-page SEO, schema markup, and fast load times baked in",
      "WCAG 2.1 accessibility best practices",
      "Ongoing hosting, maintenance, and content updates",
    ],
    seoDescription: "Custom, mobile-friendly website design for small businesses. Built to rank on Google, capture leads, and grow with you. Book your free consultation today!",
    heroSubtitle: "Fast, mobile-friendly, search-optimized websites built by a team that answers the phone.",
    heroCtaLabel: "SCHEDULE A FREE CONSULTATION",
    heroBestFor: "Best for small businesses, professional services, contractors, and local shops that need a website that actually works for them.",
  },
  {
    slug: "software-integration",
    title: "Software Integration & Workflow Automation for Small Businesses",
    shortTitle: "Software Integration",
    excerpt: "Stop copying and pasting between apps. We connect your tools so your business runs on autopilot — accurate data, faster work, fewer mistakes.",
    description:
      "The average small business uses 30+ different apps — and most of them don't talk to each other. Your team ends up rekeying the same info into your CRM, accounting software, email tool, and spreadsheets every day. That's lost hours, duplicate data, and missed follow-ups. SimpleTech connects your software using tools like Zapier, Make, Microsoft Power Automate, and direct APIs, so customer data flows automatically, reports build themselves, and your team gets their time back.",
    icon: Workflow,
    highlights: [
      "Zapier, Make, Power Automate & custom API integrations",
      "CRM ↔ accounting ↔ email/marketing workflows",
      "Plain-English documentation of every automation",
      "Ongoing monitoring so workflows keep running",
    ],
    seoDescription: "Connect your apps and automate the busywork. Software integration & workflow automation for small businesses — Zapier, Make, CRMs, QuickBooks & more.",
    heroSubtitle: "Stop copying and pasting between apps. We connect your tools so your business runs on autopilot.",
    heroCtaLabel: "SCHEDULE A FREE AUTOMATION REVIEW",
    heroBestFor: "Best for small businesses, professional services, agencies, and any team stuck doing the same manual task more than five times a week.",
  },
  {
    slug: "tech-support-for-seniors",
    title: "Patient, Friendly Tech Help for Seniors",
    shortTitle: "Tech Support for Seniors",
    excerpt: "Patient, in-home and remote tech help for seniors across the Inland Empire and Orange County. No rushing. No jargon.",
    description:
      "Technology should make life easier — not stressful. SimpleTech helps seniors stay connected to family, safe from scams, and confident with their devices. Whether you want to FaceTime your grandkids, set up email on your new phone, fix a slow computer, or figure out why the printer stopped working — we sit with you, explain things in plain English, and never rush. We come to your home, help you over the phone, or connect remotely — whichever you're most comfortable with.",
    icon: UserRound,
    highlights: [
      "Patient, never-rushed one-on-one help",
      "In-home visits, phone, or remote screen-share",
      "Scam-call, email, and text protection",
      "New device setup and friendly tutoring",
    ],
    seoDescription: "Friendly, patient tech help for seniors — smartphones, computers, email, Wi-Fi & online safety. In-home visits or remote help. No rushing, no jargon.",
    heroSubtitle: "No rushing. No talking down to you. No confusing jargon. Just real help — in your home or remotely, across the Inland Empire and Orange County.",
    heroCtaLabel: "CALL\n(909) 255-9112 OR SCHEDULE A VISIT",
    heroBestFor: "Best for seniors who want to enjoy their devices without frustration — and for family members looking for a trusted, patient helper for their parents or grandparents.",
  },
  {
    slug: "ada-compliant-website",
    title: "ADA Compliant Website Audits & WCAG 2.1 Remediation",
    shortTitle: "ADA Website Compliance",
    excerpt: "Protect your business from ADA lawsuits and open your site to every customer with a real WCAG 2.1 AA audit — no overlays, no shortcuts.",
    description:
      "ADA website lawsuits hit over 4,000 small businesses last year — and overlay widgets won't save you. SimpleTech runs real WCAG 2.1 Level AA audits, fixes the issues at the code level, and gives you a written conformance statement your attorney can rely on. Whether you've received a demand letter, want to get ahead of one, or simply believe your site should work for every customer — we'll get you compliant and keep you that way.",
    icon: Accessibility,
    highlights: [
      "Full WCAG 2.1 Level AA audit with prioritized report",
      "Real screen-reader, keyboard, and contrast testing — no overlay widgets",
      "Code-level remediation by accessibility-trained developers",
      "Demand-letter & lawsuit response (we work with your attorney)",
      "Written WCAG 2.1 AA conformance statement on completion",
      "Ongoing monitoring so new pages stay compliant",
    ],
    seoDescription: "Real ADA website compliance audits & WCAG 2.1 AA remediation for small businesses. No overlay widgets. Demand-letter response available. Free 20-minute review.",
    heroSubtitle: "Real WCAG 2.1 AA audits and code-level fixes. No overlay widgets. No shortcuts.",
    heroCtaLabel: "GET A FREE ACCESSIBILITY REVIEW",
    heroBestFor: "Best for small businesses, e-commerce stores, medical and legal practices that want to avoid ADA lawsuits — and for anyone who's already received a demand letter.",
  },
  {
    slug: "cyber-security",
    title: "Cybersecurity for Small & Medium Businesses",
    shortTitle: "Cyber Security",
    excerpt: "Stop ransomware, phishing, and BEC. MDR, training, and HIPAA / cyber-insurance compliance for businesses without an in-house IT team.",
    description:
      "60% of small businesses that suffer a cyberattack close within six months. Most of the attacks that take them down — phishing emails, ransomware, business email compromise, leaked passwords — are entirely preventable with the right layered controls. SimpleTech builds practical, affordable cybersecurity programs for small and medium businesses, including 24/7 monitoring (MDR), phishing defense, multi-factor authentication, endpoint protection (EDR), employee training, and compliance support for HIPAA, PCI, SEC, and California's CCPA/CPRA. We also help you meet the cybersecurity requirements your cyber-insurance policy demands at renewal.",
    icon: ShieldCheck,
    highlights: [
      "24/7 Managed Detection & Response (MDR)",
      "Multi-factor authentication & endpoint protection (EDR)",
      "Phishing simulations and security awareness training",
      "Cyber-insurance & HIPAA / PCI / SEC / CCPA compliance support",
    ],
    seoDescription: "Cybersecurity for small & medium businesses. Protect against ransomware, phishing & BEC. HIPAA compliance, MDR & training. Free risk assessment.",
    heroSubtitle: "Stop ransomware, phishing, and business email compromise before they stop you. Protection, training, and compliance — built for businesses without an in-house IT team.",
    heroCtaLabel: "GET A FREE CYBERSECURITY RISK ASSESSMENT",
    heroBestFor: "Best for small & medium businesses in regulated industries — healthcare, legal, financial services, insurance, real estate, and any business that holds customer or payment data.",
  },
  {
    slug: "tech-support-for-realtors",
    title: "Tech Support for Realtors",
    shortTitle: "Tech Support for Realtors",
    excerpt: "Fast, reliable tech support for real estate agents, teams, and brokerages — remote help, CRM and MLS fixes, and same-day response.",
    description:
      "Every missed call, broken email, or slow laptop can cost you a lead — and in real estate, lost leads mean lost commissions. SimpleTech provides fast, reliable tech support for realtors who need their technology to work as hard as they do. We support solo agents, real estate teams, and full brokerages with remote and on-site help that fits your schedule, not ours. No confusing jargon. No three-day callbacks. Just dependable tech support from a team that picks up the phone.",
    icon: Home,
    highlights: [
      "Same-day response — most issues fixed remotely within the hour",
      "CRM, MLS, eSignature & transaction management support",
      "Google Workspace / Microsoft 365 email & calendar setup",
      "Data backup, password management & malware removal",
    ],
    seoDescription: "Fast, reliable tech support for real estate agents and brokerages. CRM, MLS, email, devices, and security — remote and on-site help from a team that answers the phone.",
    heroSubtitle: "We keep realtors connected and deals closing",
    heroCtaLabel: "CALL\n(909) 255-9112",
    heroBestFor: "Best for independent real estate agents, real estate teams, brokers, property managers, and real estate office staff.",
  },
  {
    slug: "tech-support-for-insurance-agents",
    title: "Tech Support for Insurance Agents",
    shortTitle: "Insurance Agency IT",
    excerpt: "Compliant, secure tech support for insurance agents — device management, AMS support, data security, and equipment onboarding.",
    description:
      "Insurance agencies run on trust and compliance. SimpleTech keeps your devices audit-ready, your client data protected, and your team equipped to serve clients without interruption. We handle device compliance, equipment procurement and onboarding, AMS and carrier portal support, layered security, and remote help — so your agency runs smoothly and stays compliant.",
    icon: FileCheck,
    highlights: [
      "Device compliance audits & ongoing monitoring",
      "Equipment procurement, configuration & onboarding",
      "AMS, quoting tool & carrier portal support",
      "Encrypted backups, MFA & client data protection",
    ],
    seoDescription: "Compliant, reliable tech support for insurance agents and agencies — device management, AMS support, data security, and remote help.",
    heroSubtitle: "Compliant. Secure. Always on.",
    heroCtaLabel: "CALL\n(909) 255-9112",
    heroBestFor: "Best for independent insurance agents, captive agency offices, multi-line agencies & brokerages, and agency support staff.",
  },
  {
    slug: "tech-support-for-estate-planning-attorneys",
    title: "Tech Support for Estate Planning Attorneys",
    shortTitle: "Estate Planning Attorney IT",
    excerpt: "Secure, compliant IT support for estate planning attorneys — legacy software integration, data security, and document management.",
    description:
      "Estate planning attorneys handle some of life's most sensitive decisions. SimpleTech provides dedicated tech support for estate planning attorneys — modernizing legacy software, securing client data, and keeping practice management tools running so attorneys can focus on their clients.",
    icon: Scale,
    highlights: [
      "Legacy software integration & modernization",
      "Document management & workflow optimization",
      "Encrypted storage, MFA & access controls",
      "Clio, MyCase, WealthCounsel & HotDocs support",
    ],
    seoDescription: "Reliable IT support for estate planning attorneys — legacy software integration, data security, document management, and ongoing managed IT.",
    heroSubtitle: "Secure. Compliant. Modernized — without losing what works.",
    heroCtaLabel: "CALL\n(909) 255-9112",
    heroBestFor: "Best for solo estate planning attorneys, small estate & trust law firms, elder law practices, and probate offices.",
  },
  {
    slug: "tech-support-for-financial-advisors",
    title: "Tech Support for Financial Advisors",
    shortTitle: "Financial Advisor IT",
    excerpt: "Secure, compliant IT support for financial advisors — SEC/FINRA-aware cybersecurity, data protection, and software support.",
    description:
      "Financial advisors carry their clients' financial futures. The technology that supports that responsibility needs to be secure, compliant, and completely reliable. SimpleTech provides dedicated tech support for financial advisors — modernizing practice management, securing client data, and keeping financial planning software running so advisors can focus on their clients.",
    icon: LineChart,
    highlights: [
      "SEC and FINRA cybersecurity compliance support",
      "Redtail, Wealthbox, eMoney, Orion & Morningstar support",
      "Encrypted backups, MFA & client data protection",
      "Records retention & document management systems",
    ],
    seoDescription: "Secure, compliant tech support for financial advisors — SEC/FINRA-aware IT, data protection & software support from SimpleTech.",
    heroSubtitle: "Secure. Compliant. Fiduciary-grade.",
    heroCtaLabel: "CALL\n(909) 255-9112",
    heroBestFor: "Best for independent RIAs, broker-dealer affiliated advisors, fee-only planners, and wealth management offices.",
  },
];


export const getService = (slug: string) => services.find((s) => s.slug === slug);
